import { createStyles } from 'antd-style'

import { STICKY_OFFSET_VARIABLE, STUCK_HEADER_ATTRIBUTE } from './hooks'

export const ROW_HEIGHT = 48
const STICKY_HEADER_Z_INDEX = 3
const EDGE_SHADOW_PX = 8

export const useTreadmillStyles = createStyles(({ token }) => ({
  uniformRows: {
    '.ant-table-tbody > tr > td': {
      height: ROW_HEIGHT,
      verticalAlign: 'middle',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis'
    },

    '.ant-table-tbody > tr > td.ant-table-cell-fix': {
      overflow: 'visible'
    },

    '.ant-table-tbody > tr > td *': {
      minWidth: 0,
      maxWidth: '100%',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis'
    },

    '.ant-table-tbody > tr > td :is(*:has(svg), *:has(img), *:has(.ant-avatar))':
      {
        minWidth: 'auto',
        overflow: 'visible'
      },

    '.ant-table-tbody > tr > td :is(svg, img, .ant-avatar)': {
      flex: 'none',
      minWidth: 'auto',
      maxWidth: 'none'
    }
  },
  placeholderBar: {
    display: 'block',
    width: '40%',
    maxWidth: 240,
    height: token.fontSizeSM,
    borderRadius: token.borderRadiusSM,
    backgroundColor: token.colorFillSecondary
  },
  headerLabel: {
    display: 'flex',
    alignItems: 'center',
    minWidth: 0,
    width: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',

    '& > *': {
      minWidth: 0
    }
  },
  headerGrip: {
    cursor: 'grab',
    touchAction: 'none',
    userSelect: 'none',
    WebkitUserSelect: 'none',

    '&:active': {
      cursor: 'grabbing'
    },

    'th.ant-table-column-has-sorters &, th.ant-table-column-has-sorters &:active':
      {
        cursor: 'pointer'
      }
  },
  draggingHeaderCell: {
    opacity: 0.5,
    zIndex: 4
  },
  resizeHandle: {
    position: 'absolute',
    insetBlock: 0,
    insetInlineEnd: -4,
    width: 9,
    cursor: 'col-resize',
    zIndex: 3,
    touchAction: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',

    '&::after': {
      content: '""',
      width: token.lineWidth,
      height: '40%',
      borderRadius: token.lineWidth,
      backgroundColor: 'transparent',
      transition: `background-color ${token.motionDurationFast}`
    },

    '&:hover::after': {
      width: token.lineWidthBold,
      height: '60%',
      backgroundColor: token.colorPrimary
    },

    'th:last-child > &': {
      insetInlineEnd: 0
    }
  },
  resizeHandleActive: {
    '&::after, &:hover::after': {
      width: token.lineWidthBold + token.lineWidth,
      height: '80%',
      backgroundColor: token.colorPrimary
    }
  },
  treadmillScroller: {
    overflowX: 'auto',
    backgroundColor: token.colorBgContainer
  },
  treadmillCanvas: {
    position: 'relative'
  },
  clickableRow: {
    cursor: 'pointer'
  },
  treadmillRow: {
    position: 'absolute',
    top: 0,
    left: 0,
    display: 'flex',
    alignItems: 'stretch',
    width: '100%',
    contain: 'layout paint style',
    backgroundColor: token.colorBgContainer,
    borderBlockEnd: `${token.lineWidth}px ${token.lineType} ${token.colorBorderSecondary}`,

    '&:hover > div': {
      backgroundImage: `linear-gradient(${token.colorFillQuaternary}, ${token.colorFillQuaternary})`
    },

    '&:has(.ant-checkbox-checked) > div': {
      backgroundImage: `linear-gradient(${token.controlItemBgActive}, ${token.controlItemBgActive})`
    },

    '&:has(.ant-checkbox-checked):hover > div': {
      backgroundImage: `linear-gradient(${token.controlItemBgActiveHover}, ${token.controlItemBgActiveHover})`
    }
  },
  treadmillSpacer: {
    flex: 'none',
    alignSelf: 'stretch',
    backgroundColor: token.colorBgContainer
  },
  treadmillCell: {
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    flex: 'none',
    minWidth: 0,
    paddingInline: token.paddingSM,
    paddingBlock: token.paddingXXS,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    backgroundColor: token.colorBgContainer,

    '& *': {
      minWidth: 0,
      maxWidth: '100%',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis'
    },

    '& :is(*:has(svg), *:has(img), *:has(.ant-avatar))': {
      minWidth: 'auto',
      overflow: 'visible'
    },

    '& :is(svg, img, .ant-avatar)': {
      flex: 'none',
      minWidth: 'auto',
      maxWidth: 'none'
    }
  },
  treadmillSelectionCell: {
    position: 'sticky',
    left: 0,
    zIndex: 2,
    justifyContent: 'center'
  },
  treadmillLeftCell: {
    position: 'sticky',
    zIndex: 2
  },
  treadmillRightCell: {
    position: 'sticky',
    zIndex: 2,
    cursor: 'auto'
  },
  treadmillLeftEdge: {
    transition: `box-shadow ${token.motionDurationMid}`,
    '.ant-table-fix-start-shadow-show &': {
      boxShadow: `${EDGE_SHADOW_PX}px 0 ${EDGE_SHADOW_PX}px -${EDGE_SHADOW_PX}px ${token.colorFill}`
    }
  },
  treadmillRightEdge: {
    transition: `box-shadow ${token.motionDurationMid}`,
    '.ant-table-fix-end-shadow-show &': {
      boxShadow: `-${EDGE_SHADOW_PX}px 0 ${EDGE_SHADOW_PX}px -${EDGE_SHADOW_PX}px ${token.colorFill}`
    }
  },
  resizableHeader: {
    '.ant-table-thead th': {
      position: 'relative'
    },

    '.ant-table-thead .ant-table-cell': {
      whiteSpace: 'nowrap'
    },

    '.ant-table-thead .ant-table-column-title': {
      overflow: 'hidden',
      textOverflow: 'ellipsis'
    }
  },
  edgeShadows: {
    '&& .ant-table-cell-fix-start-shadow-show::after': {
      boxShadow: `inset ${EDGE_SHADOW_PX}px 0 ${EDGE_SHADOW_PX}px -${EDGE_SHADOW_PX}px ${token.colorFill}`
    },
    '&& .ant-table-cell-fix-end-shadow-show::after': {
      boxShadow: `inset -${EDGE_SHADOW_PX}px 0 ${EDGE_SHADOW_PX}px -${EDGE_SHADOW_PX}px ${token.colorFill}`
    },
    '.ant-table-header': {
      transition: `box-shadow ${token.motionDurationMid}`
    },
    [`[${STUCK_HEADER_ATTRIBUTE}] & .ant-table-header`]: {
      boxShadow: `0 ${EDGE_SHADOW_PX}px ${EDGE_SHADOW_PX}px -${EDGE_SHADOW_PX}px ${token.colorFill}`
    }
  },
  virtualHeader: {
    '.ant-table-header': {
      position: 'sticky',
      top: `var(${STICKY_OFFSET_VARIABLE}, 0px)`,
      zIndex: STICKY_HEADER_Z_INDEX
    },

    '.ant-table table': {
      minWidth: '0 !important'
    },

    '.ant-table-header colgroup col:last-child': {
      width: 'auto !important'
    }
  },
  treadmillEmpty: {
    display: 'flex',
    justifyContent: 'center',
    paddingBlock: token.paddingXL,
    backgroundColor: token.colorBgContainer
  }
}))
